import { Store } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { LayoutDashboard, ShoppingCart, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { barClubViewSearchViewAllRequest } from "../redux/barclub/BarClubAction";
import { customerSearchViewAllRequest } from "../redux/customer/CustomerAction";
import { wholesalerSearchViewAllRequest } from "../redux/wholesaler/WholesalerAction";
import {
  wholesalerMonthReportRequest,
  wholesalerReportSearchRequest,
} from "../redux/reports/ReportAction";
import { alcoholPercentageViewRequest } from "../redux/typeOfAlcohol/TypeOfAlcoholAction";
import { APP_LINK } from "./global/MenuItems";
import { adminDashboardStyles } from "./components/reportStyles";
import {
  barMonthReportRequest,
  barReportSearchRequest,
} from "../redux/barReport/BarReportAction";
import { format, parseISO, isValid } from "date-fns";
import { CircleLoader } from "react-spinners";

const Dashboard = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [showScroll, setShowScroll] = useState(false);

  const id = useSelector(
    (state) => state?.values?.loginValues?.[0]?.wholesaler_id
  );

  const barClubViewAllState = useSelector(
    (state) => state?.barClub?.viewAllSearchBarClub || {}
  );
  const barClubList = barClubViewAllState?.data?.data || [];
  const barTotalRecords = barClubViewAllState?.data?.totalRecords || 0;

  useEffect(() => {
    dispatch(barClubViewSearchViewAllRequest());
  }, [dispatch]);

  const customerViewAllState = useSelector(
    (state) => state.customer?.viewAllSearchCustomer || {}
  );
  const customerList = customerViewAllState?.data?.data || [];
  const customerTotalRecords = customerViewAllState?.data?.totalRecords || 0;

  useEffect(() => {
    dispatch(customerSearchViewAllRequest());
  }, [dispatch]);

  const wholesalerViewAllState = useSelector(
    (state) => state?.wholesaler?.viewAllSearchWholesaler || {}
  );
  const wholesalerList = wholesalerViewAllState?.data?.data || [];

  const alcoholPercentageViewState = useSelector(
    (state) => state?.TypeAlcohol?.viewAlcoholPercentage || {}
  );

  const alcoholPercentageList = alcoholPercentageViewState?.data?.data || [];

  useEffect(() => {
    dispatch(alcoholPercentageViewRequest());
  }, [dispatch, id]);

  const wholesalerTotalRecords =
    wholesalerViewAllState?.data?.totalRecords || 0;

  useEffect(() => {
    dispatch(wholesalerSearchViewAllRequest());
  }, [dispatch]);

  const barReportViewState = useSelector(
    (state) => state?.barReport?.searchBarReports || {}
  );

  useEffect(() => {
    dispatch(barReportSearchRequest());
  }, [dispatch]);

  const barMonthViewAllState = useSelector(
    (state) => state?.barReport?.monthBarReports || {}
  );

  const barMonthWiseReport =
    barMonthViewAllState?.data?.data?.totals?.monthWiseReport || [];

  useEffect(() => {
    dispatch(barMonthReportRequest());
  }, [dispatch]);

  const wholesalerReportViewAllState = useSelector(
    (state) => state?.consumption?.searchWholesalerReports || {}
  );

  const wholesalerReportList = wholesalerReportViewAllState?.data || [];

  const allWholesalersDayWiseReport =
    wholesalerReportViewAllState?.data?.totals?.allWholesalersDayWiseReport ||
    [];

  const preprocessData = (data) => {
    if (!data?.data || !Array.isArray(data.data)) {
      return [];
    }

    return data.data.flatMap((item, index) => {
      if (
        !Array.isArray(item?.dayWiseReport) ||
        item.dayWiseReport.length === 0
      ) {
        return [];
      }

      let paymentStatus = "Paid";

      return item.dayWiseReport.map((report, i) => ({
        id: `${item?.id}-${i + 1}`,
        Name: item?.name || "N/A",
        Date: report?.date || "N/A",
        TotalBottlesSold: Number(report?.totalBottlesSold) || 0,
        Transactions: report?.transactions || 0,
        MostSoldBrand: report?.mostOrderedBrand || "Unknown",
        TodaySales: report?.transactions || 0,
        PaymentStatus: paymentStatus,
      }));
    });
  };

  const transactionData = preprocessData(wholesalerReportList);

  const topFiveByDate = transactionData
    .sort((a, b) => new Date(b.Date) - new Date(a.Date))
    .slice(0, 5);

  useEffect(() => {
    dispatch(wholesalerReportSearchRequest());
  }, [dispatch]);

  const monthWholesalerViewAllState = useSelector(
    (state) => state?.consumption?.monthWholesalerReports || {}
  );

  const allWholesalersMonthWiseReport =
    monthWholesalerViewAllState?.data?.totals?.allWholesalersMonthlyReport ||
    [];

  useEffect(() => {
    dispatch(wholesalerMonthReportRequest());
  }, [dispatch]);

  const handleTableClick = (e) => {
    e.stopPropagation();
    setShowScroll(true);
  };

  const handleOutsideClick = () => {
    setShowScroll(false);
  };

  const getTopAlcoholCategories = (list, topN = 5) => {
    if (!Array.isArray(list)) return [];

    return [...list]
      .sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))
      .slice(0, topN)
      .map((item, index) => ({
        name: item.type_of_alcohol,
        percentage: parseFloat(item.percentage),
        color: getBarColor(index),
      }));
  };

  const todayDate = new Date().toISOString().split("T")[0];

  const todaySalesEntry = allWholesalersDayWiseReport.find(
    (item) => item.date === todayDate
  );

  const todayTotalSales = todaySalesEntry?.transactions || 0;

  const monthMap = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    10: "October",
    11: "November",
    12: "December",
  };

  const allMonths = Object.entries(monthMap).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const normalizedData = allMonths.map(([monthNum, monthName]) => {
    const paddedMonth = monthNum.toString().padStart(2, "0");
    const monthKey = `2025-${paddedMonth}`;

    const barData = barMonthWiseReport.find((item) => item.month === monthKey);
    const wholesaleData = allWholesalersMonthWiseReport.find(
      (item) => item.month === monthKey
    );

    return {
      month: monthName,
      transactions: wholesaleData?.monthlyTransactions || 0,
      totalShots: barData?.totalShots || 0,
    };
  });

  const CombinedTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const transactions = payload.find((p) => p.dataKey === "transactions");
      const totalShots = payload.find((p) => p.dataKey === "totalShots");

      return (
        <Paper sx={adminDashboardStyles.dashboardCardPaper}>
          <Typography variant="subtitle2" fontWeight="bold">
            {label}
          </Typography>
          <div>
            {transactions && (
              <Typography color="#3182CE">
                Wholesaler Transactions: {transactions.value}
              </Typography>
            )}
            {totalShots && (
              <Typography color="#ab47bc">
                Bar Total Shots: {totalShots.value}
              </Typography>
            )}
          </div>
        </Paper>
      );
    }

    return null;
  };

  const getBarColor = (index) => {
    const colors = ["#42a5f5", "#EC6B56", "#FFC154", "#47B39C", "#8E44AD"];
    return colors[index % colors.length];
  };

  const productCategories = getTopAlcoholCategories(alcoholPercentageList);

  const cardColors = ["#0074D9", "#ffffff", "#0074D9", "#ffffff"];

  const metrics = [
    {
      icon: <Store style={adminDashboardStyles.iconStyles} />,
      label: "Total Wholesalers",
      path: APP_LINK.ONBOARDEDWHOLESALER,
      value: (
        <Typography variant="h6" sx={adminDashboardStyles.metricsStyles}>
          {wholesalerTotalRecords}
        </Typography>
      ),
    },
    {
      icon: <ShoppingCart style={adminDashboardStyles.iconStyles} />,
      label: "Total Sales",
      path: APP_LINK.ADMINREPORT,
      value: (
        <Typography variant="h6" sx={adminDashboardStyles.metricsStyles1}>
          {`${todayTotalSales.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
        </Typography>
      ),
    },
    {
      icon: <LayoutDashboard style={adminDashboardStyles.iconStyles} />,
      label: "Total Bars",
      path: APP_LINK.BARCLUBAGGREGATION,
      value: (
        <Typography variant="h6" sx={adminDashboardStyles.metricsStyles}>
          {barTotalRecords}
        </Typography>
      ),
    },
    {
      icon: <Users style={adminDashboardStyles.iconStyles} />,
      label: "Total Customers",
      path: APP_LINK.CUSTOMERS,
      value: (
        <Typography variant="h6" sx={adminDashboardStyles.metricsStyles1}>
          {customerTotalRecords}
        </Typography>
      ),
    },
  ];

  const statLoadingMap = {
    "Total Wholesalers": wholesalerViewAllState?.inProgress,
    "Total Sales": wholesalerReportViewAllState?.inProgress,
    "Total Bars": barClubViewAllState?.inProgress,
    "Total Customers": customerViewAllState?.inProgress,
  };

  return (
    <div onClick={handleOutsideClick}>
      <Box sx={adminDashboardStyles.dashboard}>
        {/* Header */}
        <Box className="headingTable" sx={adminDashboardStyles.headingStyles}>
          <h1> Admin Dashboard</h1>
        </Box>

        {/* Metrics */}
        <Grid container spacing={2}>
          {metrics.map((metric, index) => {
            const bgColor = cardColors[index % cardColors.length];
            const isBlue = bgColor !== "#ffffff";
            const isLoading = statLoadingMap[metric.label];

            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={4}
                  sx={{
                    ...adminDashboardStyles.metricsPaper,
                    color: isBlue ? "#fff" : "#2f4cdd",
                    backgroundColor: bgColor,
                  }}
                  onClick={() => metric.path && navigate(metric.path)}
                >
                  {React.cloneElement(metric.icon, {
                    style: {
                      fontSize: 30,
                      color: isBlue ? "#ffffff" : "#2f4cdd",
                    },
                  })}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mt: 1,
                      fontWeight: "bold",
                      color: isBlue ? "#ffffff" : "#2f4cdd",
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Box mt={1}>
                    {isLoading ? (
                      <Box display="flex" justifyContent="center">
                        <CircleLoader
                          size={28}
                          color={isBlue ? "#fff" : "#2f4cdd"}
                        />
                      </Box>
                    ) : (
                      metric.value
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Tables */}
        {/* Wholesalers */}
        <Grid container spacing={3} mt={2}>
          {/* Wholesalers Table */}
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={3}
              sx={adminDashboardStyles.wholesalerTablePaper}
              onClick={handleTableClick}
            >
              <Typography
                variant="h6"
                color="textPrimary"
                fontWeight="bold"
                gutterBottom
                sx={adminDashboardStyles.wholesalerTableTypography}
              >
                Wholesalers
              </Typography>
              <TableContainer
                sx={{
                  overflowY: showScroll ? "auto" : "hidden",
                  flex: 1,
                }}
              >
                <Table>
                  <TableHead sx={adminDashboardStyles.tableHead}>
                    <TableRow>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Register No
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Wholesaler
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Contact
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wholesalerViewAllState?.inProgress ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircleLoader size={50} color="#0458d3" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : Array.isArray(wholesalerList) &&
                      wholesalerList.length > 0 ? (
                      wholesalerList.slice(0, 5).map((data) => (
                        <TableRow key={data?.id}>
                          <TableCell>{data?.reg_no || "N/A"}</TableCell>
                          <TableCell>{data?.name || "N/A"}</TableCell>
                          <TableCell>{data?.contact || "N/A"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Alcohol Categories */}
          <Grid item xs={12} sm={6}>
            <Paper elevation={3} sx={adminDashboardStyles.alcoholPaper}>
              <Typography
                variant="h6"
                color="textPrimary"
                fontWeight="bold"
                gutterBottom
                sx={adminDashboardStyles.typographyColor}
              >
                Alcohol Categories
              </Typography>

              <Box mt={3} position="relative" minHeight="150px">
                {alcoholPercentageViewState?.inProgress ? (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bgcolor="rgba(255, 255, 255, 0.6)"
                    zIndex={10}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CircleLoader size={50} color="#0458d3" />
                  </Box>
                ) : productCategories && productCategories.length > 0 ? (
                  productCategories.map((category) => (
                    <Box key={category.name} mb={2}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="textSecondary">
                          {category.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {category.percentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={category.percentage}
                        sx={{
                          height: 8,
                          borderRadius: 5,
                          bgcolor: "#E0E0E0",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: category.color,
                          },
                        }}
                      />
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    minHeight="100px"
                  >
                    <Typography color="textSecondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Sales Chart */}
        <Grid container spacing={3}>
          {/* Comprehensive Sales Chart */}
          <Grid item xs={12} md={12}>
            <Paper elevation={3} sx={adminDashboardStyles.salesChartPaper}>
              <Typography
                variant="h6"
                color="textPrimary"
                fontWeight="bold"
                gutterBottom
                sx={adminDashboardStyles.salesChartTypography}
              >
                Sales Performance Metrics
              </Typography>

              {barMonthViewAllState?.inProgress ||
              monthWholesalerViewAllState?.inProgress ? (
                <Box
                  height={350}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircleLoader size={50} color="#0458d3" />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={normalizedData}>
                    <CartesianGrid stroke="#E0E0E0" strokeDasharray="5 5" />
                    <XAxis dataKey="month" stroke="#757575" interval={0} />

                    <YAxis
                      yAxisId="left"
                      stroke="#3182CE"
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#ab47bc"
                    />

                    <Tooltip content={<CombinedTooltip />} />
                    <Legend />

                    <Bar
                      yAxisId="left"
                      dataKey="transactions"
                      barSize={20}
                      fill="#3182CE"
                      name="Wholesaler Transactions"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="totalShots"
                      barSize={20}
                      fill="#ab47bc"
                      name="Bar Total Shots"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
        {/* Tables */}
        {/* Bar */}
        <Grid container spacing={1}>
          {/* Bar Inventory Table */}
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={3}
              sx={adminDashboardStyles.barTablePaper}
              onClick={handleTableClick}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={adminDashboardStyles.typographyColor}
              >
                Bar Inventory
              </Typography>
              <TableContainer
                sx={{
                  overflowY: showScroll ? "auto" : "hidden",
                  flex: 1,
                }}
              >
                <Table>
                  <TableHead sx={adminDashboardStyles.tableHead}>
                    <TableRow>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Register No
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Wholesaler
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Bar/Club
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Contact
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {barClubViewAllState?.inProgress ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircleLoader size={50} color="#0458d3" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : Array.isArray(barClubList) && barClubList.length > 0 ? (
                      barClubList.slice(0, 5).map((data) => (
                        <TableRow key={data?.id}>
                          <TableCell>{data?.reg_no || "N/A"}</TableCell>
                          <TableCell>
                            {data?.WholeSaler?.wholesaler_name || "N/A"}
                          </TableCell>
                          <TableCell>{data?.name || "N/A"}</TableCell>
                          <TableCell>{data?.contact || "N/A"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Customer List Table */}
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={3}
              sx={adminDashboardStyles.customerTablePaper}
              onClick={handleTableClick}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={adminDashboardStyles.typographyColor}
              >
                Customer List
              </Typography>
              <TableContainer
                sx={{
                  overflowY: showScroll ? "auto" : "hidden",
                  flex: 1,
                }}
              >
                <Table>
                  <TableHead sx={adminDashboardStyles.tableHead}>
                    <TableRow>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Register No
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Customer
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Contact
                      </TableCell>
                      <TableCell sx={adminDashboardStyles.tableCell}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerViewAllState?.inProgress ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircleLoader size={50} color="#0458d3" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : Array.isArray(customerList) &&
                      customerList.length > 0 ? (
                      customerList.slice(0, 5).map((data) => {
                        const isActive = data?.status === 1;
                        const statusLabel = isActive ? "Active" : "Inactive";
                        const statusColor = isActive ? "#4caf8e" : "#f44336";

                        return (
                          <TableRow key={data?.id}>
                            <TableCell>{data?.reg_no || "N/A"}</TableCell>
                            <TableCell>
                              {data?.first_name + " " + data?.last_name ||
                                "N/A"}
                            </TableCell>
                            <TableCell>{data?.phone || "N/A"}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  ...adminDashboardStyles.statusLabel,
                                  backgroundColor: statusColor,
                                }}
                              >
                                {statusLabel}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Wholesaler Transactions */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={adminDashboardStyles.transactions}>
            <Typography
              variant="h6"
              color="textPrimary"
              fontWeight="bold"
              gutterBottom
              sx={adminDashboardStyles.typographyColor}
            >
              Transactions
            </Typography>
            <TableContainer>
              <Table>
                <TableHead sx={adminDashboardStyles.tableHead}>
                  <TableRow>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      S.No
                    </TableCell>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      Wholesaler
                    </TableCell>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      Amount
                    </TableCell>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      Total Bottles Sold
                    </TableCell>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      Most Ordered Brand
                    </TableCell>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      Payment Status
                    </TableCell>
                    <TableCell sx={adminDashboardStyles.tableCell}>
                      Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wholesalerReportViewAllState?.inProgress ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                        >
                          <CircleLoader size={50} color="#0458d3" />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : Array.isArray(transactionData) &&
                    transactionData.length > 0 ? (
                    transactionData.map((retailer, index) => (
                      <TableRow key={retailer.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{retailer.Name || "N/A"}</TableCell>
                        <TableCell>
                          {Number(retailer.Transactions).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {retailer.TotalBottlesSold || "N/A"}
                        </TableCell>
                        <TableCell>{retailer.MostSoldBrand || "N/A"}</TableCell>
                        <TableCell sx={adminDashboardStyles.transactionsCell}>
                          <span
                            style={{
                              color: "#fff",
                              backgroundColor:
                                retailer.PaymentStatus === "Pending"
                                  ? "orange"
                                  : retailer.PaymentStatus === "Due"
                                  ? "#c0c5d2"
                                  : "#4caf8e",
                              padding: "2px 8px",
                              borderRadius: "4px",
                            }}
                          >
                            {retailer.PaymentStatus}
                          </span>
                        </TableCell>
                        <TableCell>
                          {retailer.Date && isValid(parseISO(retailer.Date))
                            ? format(parseISO(retailer.Date), "dd MMM yyyy")
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Box>
    </div>
  );
};

export default Dashboard;
